/**
 * cp-catalog.ts
 *
 * W66.C — Methodology Workspace
 * Canonical CP definitions for the CP Catalog horizon.
 *
 * W66.X24 — CP names and fullNames are now derived from the single canonical
 *           source HELIX_CHECKPOINTS (helix-checkpoints.ts, locked 2026-05-26).
 *           This removes the prior hardcoded name list that had drifted out of
 *           canon (CP1 "Industry Context", CP2 "Compelling Pain"). The H1 CP
 *           Catalog now matches H2 Process Flow and H4 Helix exactly.
 *
 * Source authority: helix-checkpoints.ts (names, weights), CANON_VISUALS_LOCKED.md.
 *
 * This module adds only catalog-specific presentation fields on top of canon:
 * description, doctrineNote, isDisqualifier. It does NOT redefine CP names.
 *
 * LOCKED by Bryan K. Short, Founder and CEO, Mi6 Group — 2026-05-31.
 *
 * Weights are owned by HELIX_CHECKPOINTS and sum to 100. Do NOT change without
 * explicit Bryan approval.
 * M-PACT always hyphenated. Em-dash format: CP{N} — {Name}.
 */

import { HELIX_CHECKPOINTS, getCheckpoint } from "@/lib/helix-checkpoints";

export interface CpCatalogEntry {
  /** Checkpoint number 1–10 */
  number: number;
  /** Short label only — e.g. "Project". Sourced from canon. */
  name: string;
  /** Full em-dash label — CP{N} — {Name}. Sourced from canon. */
  fullName: string;
  /** Weight out of 100. Sourced from canon. */
  weight: number;
  /** One-sentence description of what this checkpoint qualifies. */
  description: string;
  /**
   * Doctrine note — the key question the rep must answer at this checkpoint.
   * Kept to two sentences max so it fits a compact card.
   */
  doctrineNote: string;
  /**
   * Whether a deal must satisfy this checkpoint before advancing.
   * When true the card displays a "Disqualifier" indicator.
   */
  isDisqualifier: boolean;
}

/**
 * Catalog-specific presentation content, keyed by checkpoint number.
 * Names and weights are NOT defined here — they come from HELIX_CHECKPOINTS.
 */
interface CpCatalogContent {
  number: number;
  description: string;
  doctrineNote: string;
  isDisqualifier: boolean;
}

const CP_CATALOG_CONTENT: CpCatalogContent[] = [
  {
    number: 1,
    description:
      "Confirms whether there is a real project and establishes the new-logo vs. existing-customer classification that frames every checkpoint that follows.",
    doctrineNote:
      "Is there a real project, and is this a new logo or an existing-customer expansion? CP1 sets the frame for every checkpoint that follows.",
    isDisqualifier: true,
  },
  {
    number: 2,
    description:
      "Establishes a recognized need, measured by quantified pain in the buyer's own words, validated by demonstration of the solution in their context. All three are worked together.",
    doctrineNote:
      "Does the customer have a recognized need we can address, demonstrable pain, and a provable business case? CP2 is the largest single weight because a misdiagnosis here collapses every downstream checkpoint.",
    isDisqualifier: true,
  },
  {
    number: 3,
    description:
      "Validates that real money exists — formal (named, allocated line item), discretionary (carved from an existing budget), or none (buyer must sell internally first).",
    doctrineNote:
      "Is there real money, a named source, and a validated line item? The rep must mark formal, discretionary, or no budget — each state triggers different downstream behavior.",
    isDisqualifier: false,
  },
  {
    number: 4,
    description:
      "Confirms that both a decision window and an implementation window exist and are tied to real business drivers — not arbitrary deadlines.",
    doctrineNote:
      "Is there a decision window AND an implementation window — both real? A date without a committed business driver behind it is not a real timeline.",
    isDisqualifier: false,
  },
  {
    number: 5,
    description:
      "Confirms direct access to the project or initiative authority — the sponsor whose name is on this project and who can drive it at an approval level. Not the same as a signer.",
    doctrineNote:
      "Do we have a direct line to the person who owns this initiative and can move it? Access to an influencer is not a substitute for access to the sponsor.",
    isDisqualifier: true,
  },
  {
    number: 6,
    description:
      "Identifies and engages both sign-off authorities — technical (CTO, IT, security, engineering) and financial (CFO, controller) — that must release the deal.",
    doctrineNote:
      "Who signs off technically, and who controls the financial release? Both must be named and engaged — a deal that clears technical review but lacks financial approval is not qualified.",
    isDisqualifier: false,
  },
  {
    number: 7,
    description:
      "Documents the full influence map — formal org structure and real informal power — including champions, supporters, influencers, detractors, and where authority and influence diverge.",
    doctrineNote:
      "Org charts show titles; the influence map shows who actually moves decisions. CP7 requires the rep to understand both layers and identify every person with blocking or advancing power.",
    isDisqualifier: false,
  },
  {
    number: 8,
    description:
      "Establishes where Mi6 stands against the alternatives — a real competitor, or the buyer's existing manual workarounds and infrastructure. Both are real competition.",
    doctrineNote:
      "Are we the incumbent, the challenger, or the default? The rep must name who else the customer is evaluating — including the spreadsheets and manual processes we would replace.",
    isDisqualifier: false,
  },
  {
    number: 9,
    description:
      "Identifies every IT internal and industry-specific compliance requirement that could block, delay, or reshape the deal — before they surface late in the process.",
    doctrineNote:
      "What IT internal and industry-specific compliance requirements apply to this engagement? Both must be met before legal can close; CP9 catches them early.",
    isDisqualifier: false,
  },
  {
    number: 10,
    description:
      "Surfaces every barrier to signature — contracts, SLAs, terms, and procurement requirements — and confirms a clear path through each. CP10 is paper.",
    doctrineNote:
      "Do we understand every legal and contractual hurdle, and do we have a route through them? A deal that reaches legal review unprepared stalls or dies at the finish line.",
    isDisqualifier: false,
  },
];

/**
 * The CP Catalog — canon names/weights from HELIX_CHECKPOINTS merged with
 * catalog-specific presentation content. Single source of record.
 */
export const CP_CATALOG: CpCatalogEntry[] = CP_CATALOG_CONTENT.map((content) => {
  const canon = getCheckpoint(`cp${content.number}` as Parameters<typeof getCheckpoint>[0]);
  if (!canon) {
    throw new Error(`[cp-catalog] No canonical checkpoint for CP${content.number}.`);
  }
  return {
    number: canon.number,
    name: canon.name,
    fullName: canon.fullName,
    weight: canon.weight,
    description: content.description,
    doctrineNote: content.doctrineNote,
    isDisqualifier: content.isDisqualifier,
  };
});

/**
 * Verify weights sum to 100 at import time.
 * Throws in development if the catalog is malformed.
 */
const weightSum = CP_CATALOG.reduce((acc, cp) => acc + cp.weight, 0);
if (weightSum !== 100) {
  throw new Error(
    `[cp-catalog] Weight sum is ${weightSum}, expected 100. Check HELIX_CHECKPOINTS weights.`
  );
}

/** Guard: catalog must cover all 10 canonical checkpoints. */
if (CP_CATALOG.length !== HELIX_CHECKPOINTS.length) {
  throw new Error(
    `[cp-catalog] Catalog has ${CP_CATALOG.length} entries, expected ${HELIX_CHECKPOINTS.length}.`
  );
}

export function getCpEntry(number: number): CpCatalogEntry | undefined {
  return CP_CATALOG.find((cp) => cp.number === number);
}
